import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { Card } from '@/components/ui/card';

export const ProfileManager = ({ 
  open, 
  onOpenChange, 
  profiles, 
  subscriptionLimits,
  onCreateProfile,
  onUpdateProfile,
  onDeleteProfile,
  onShowPaywall
}) => {
  const [editingProfile, setEditingProfile] = useState(null);
  const [newProfileName, setNewProfileName] = useState('');

  const handleCreate = () => {
    if (profiles.length >= subscriptionLimits.profiles) {
      onShowPaywall('profiles');
      return;
    }
    if (newProfileName.trim()) {
      onCreateProfile(newProfileName.trim());
      setNewProfileName('');
    }
  };

  const handleUpdate = (profileId) => {
    if (editingProfile && editingProfile.name.trim()) {
      onUpdateProfile(profileId, editingProfile.name.trim());
      setEditingProfile(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gestionar Perfiles</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Create new profile */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Crear nuevo perfil</h3>
            <div className="flex gap-2">
              <Input
                placeholder="Nombre del perfil (ej: Mamá, Juan, María)"
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreate()}
              />
              <Button onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Crear
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Límite: {profiles.length} / {subscriptionLimits.profiles === 999 ? '∞' : subscriptionLimits.profiles}
            </p>
          </Card>

          {/* Profile list */}
          <div className="space-y-2">
            <h3 className="font-semibold">Tus perfiles</h3>
            {profiles.map((profile) => (
              <Card key={profile.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {editingProfile?.id === profile.id ? (
                      <div className="flex gap-2">
                        <Input
                          value={editingProfile.name}
                          onChange={(e) => setEditingProfile({ ...editingProfile, name: e.target.value })}
                          onKeyPress={(e) => e.key === 'Enter' && handleUpdate(profile.id)}
                        />
                        <Button size="sm" onClick={() => handleUpdate(profile.id)}>
                          Guardar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingProfile(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <p className="font-medium">{profile.profile_name}</p>
                        <p className="text-sm text-gray-500">
                          {profile.allergens?.filter(a => a.selected).length || 0} alérgenos configurados
                        </p>
                      </div>
                    )}
                  </div>
                  {!editingProfile && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingProfile({ id: profile.id, name: profile.profile_name })}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {!profile.is_default && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onDeleteProfile(profile.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};